'use client';

import { makeFragmentData } from '@packages/gql/generated/shopping';
import type { SelectValuesFragmentFragment } from '@packages/gql/generated/shopping/graphql';
import { VariationIconFragmentFragmentDoc } from '@packages/gql/generated/shopping/VariationIconFragmentFragmentDoc';
import { Divider } from '@packages/shared/src/ui/Divider/Divider';
import { cn } from '@packages/shared/src/utils/cn/cn';
import { getIsAvailable } from '../../../helpers/getIsAvailable/getIsAvailable';
import { VariationIcon } from '../../VariationIcon/VariationIcon';

export type VariationRowProps = {
  /**
   * The GraphQL value data for the given product
   */
  value: SelectValuesFragmentFragment['values'][number];
  /**
   * indicates if one value has an image and the column has to be rendered
   */
  hasImage: boolean;
  /**
   * method that will be executed on variation click
   * @param sku the sku of the clicked variation
   * @returns void
   */
  clickHandler: (sku: SelectValuesFragmentFragment['values'][number]['sku']) => void;
};

/**
 * VariationRow component is used to display a single row of the variation selection dialog.
 * */
export const VariationRow = ({
  value: { displayText, image, isSelected, rgb, variationMainImage, sku, availability },
  hasImage,
  clickHandler,
}: VariationRowProps) => {
  const isAvailable = getIsAvailable(availability);
  // button is not clickable if selected === true
  const isClickable = !(isSelected === true);

  return (
    <>
      <button
        type="button"
        className={cn(
          'hover:bg-color-grey-light flex w-full appearance-none items-center justify-between gap-1.5 p-1.5',
          isSelected && 'bg-color-grey-light',
          isClickable ? 'cursor-pointer' : 'cursor-auto',
        )}
        onClick={() => {
          if (isClickable) {
            clickHandler(sku);
          }
        }}
        disabled={!isClickable}
      >
        {hasImage && (
          <VariationIcon
            imageSize="DIALOG"
            data={makeFragmentData(
              { rgb, image: variationMainImage || image },
              VariationIconFragmentFragmentDoc,
            )}
          />
        )}
        <span className="text-size-body2 flex-grow text-start">{displayText}</span>
        {/* is not available */}
        {!isAvailable && (
          <span className="text-size-body2 text-color-error font-semibold">
            {availability!.message}
          </span>
        )}
        {/* is available and selected */}
        {isAvailable && isSelected && (
          <span role="img" className="icon-emp-checkmark h-[32px] w-[32px]" />
        )}
        {/* is available and not selected */}
        {isAvailable && !isSelected && (
          <span role="img" className="icon-emp-arrow-medium-right h-[32px] w-[32px]" />
        )}
      </button>
      <Divider />
    </>
  );
};
